import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectCreditCardStatisticsActiveVcnCount,
  selectCreditCardStatisticsVcnCount,
} from 'src/app/Budget/redux/budget.selectors'
import {
  selectCreditCard,
  selectCreditCardPermissions,
  selectFilteredVirtualCards,
  selectUsersFullPaginatedDataFromState,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/BudgetVirtualCards/redux/budgetVirtualCards.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { useBackgroundDispatch, useQueryParamArrayHandler } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { connectedReplace } from 'src/navigation/Lnk'
import { accountVirtualCardsUrl } from 'src/navigation/urls'
import { postReportVirtualCardsDownload } from 'src/redux/actions/data/reportDownloads.actions'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

const BudgetVirtualCards: React.FC = () => {
  const creditCard = useSelector(selectCreditCard)
  const activeVcnCount = useSelector(selectCreditCardStatisticsActiveVcnCount)
  const vcnCount = useSelector(selectCreditCardStatisticsVcnCount)
  const queryParams = useSelector(selectQueryParams)
  const usersFullPaginatedData = useSelector(
    selectUsersFullPaginatedDataFromState,
  )
  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const creditCardPermissions = useSelector(selectCreditCardPermissions)

  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 130)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 130)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()
  const dispatchPostReportVirtualCardsDownload = (
    filters: VirtualCardsFiltersBody,
  ): void => {
    dispatch(
      postReportVirtualCardsDownload({
        ...filters,
        creditCardId: creditCard?.id,
      }),
    )
  }

  const fetchVirtualCards = (
    filtersBody?: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        statuses: [
          VirtualCardStatus.Active,
          VirtualCardStatus.Cancelled,
          VirtualCardStatus.Consumed,
          VirtualCardStatus.Closed,
          VirtualCardStatus.Expired,
        ],
        issued: true,
        count: 10,
        page: page,
        creditCardId: creditCard?.id,
        ...filtersBody,
      }),
    )
  }

  const canUpdateCreditCard = creditCardPermissions?.canUpdateCreditCard

  const infiniteScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCards,
  }

  // redirect to account virtual cards if source
  useEffect(() => {
    if (creditCard?.isSource) {
      dispatch(connectedReplace(accountVirtualCardsUrl(creditCard.id)))
    }
  }, [creditCard])

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('budgets virtual cards filter applied', {
        card_user: selectedFilters.card_user,
        card_type: selectedFilters.card_type,
        status: selectedFilters.status,
        search: selectedFilters.search,
        deactivating_in:
          selectedFilters.deactivating_in === undefined
            ? `anytime`
            : `${selectedFilters.deactivating_in}_days`,
      })
    }
  }, [selectedFilters])

  return (
    <div
      css={css`
        height: 100%;
      `}
      ref={ref}
      id={'ref'}
    >
      <BackgroundPageContent noStackingIndex>
        <VirtualCardsBoxAndLinks
          onDownloadClick={
            canUpdateCreditCard
              ? dispatchPostReportVirtualCardsDownload
              : undefined
          }
          virtualCards={filteredVirtualCards}
          defaultTitle={
            <ObjectBoxTitle
              title={<Trns source={'Virtual Cards Created'} />}
              static={{
                label: <Trns source={'Active'} />,
                count: activeVcnCount || 0,
              }}
            />
          }
          emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive} // TODO: investigate this further
          infiniteScrollProps={infiniteScrollProps}
          totalCount={vcnCount || 0}
          initialFilterValues={{
            sortBy: 'activeClosedUpdatedAt',
            sortDescending: false,
            recipients: useQueryParamArrayHandler(queryParams.recipients),
            keyword: queryParams.search || '',
            statuses: useQueryParamArrayHandler(queryParams.statuses),
          }}
          readOnly={!canUpdateCreditCard}
          usersFullPaginatedData={usersFullPaginatedData}
          setSelectedFilters={setSelectedFilters}
          clientHeight={clientHeight || 0}
        />
      </BackgroundPageContent>
    </div>
  )
}

export default BudgetVirtualCards
